import { IClickLogBody, useClickLogAPI } from "@/app/hooks/api_hooks/org/useClickLogAPI";
import { useAppSelector } from "@/app_redux/hooks/root_hook";
import { useCallback } from "react";

/**
 * Shared envelope for voice-to-EMR click logs: every launch point and the
 * recorder must share one screen and shape, or the funnel cannot be joined.
 * `user_id` reads `auth.user.id` — `IOAuthUser` has no `user_id`.
 */
export const VOICE_EMR_SCREEN_ID = "VOICE_TO_EMR";

/** Why an auto-start that was asked for did not begin recording. */
export type VoiceAutoStartSkipReason =
  | "not_rollout_user"
  | "start_in_flight"
  | "already_recording"
  | "session_cap"
  | "booking_not_attending"
  | "recently_completed_emr"
  | "no_emr_id"
  | "error";

export const useVoiceEMRClickLog = (defaults?: {
  clientId?: string | null;
  petId?: string | null;
}) => {
  const { execute: clickLogAPI } = useClickLogAPI();
  const userId = useAppSelector((state) => state?.auth?.userLoginInfo?.user_id);
  const userEmail = useAppSelector((state) => state?.auth?.user?.email);
  const mappedClinicId = useAppSelector((state) => state?.root?.UIStates?.mappedClinicId ?? "");

  const clientId = defaults?.clientId;
  const petId = defaults?.petId;

  return useCallback(
    (event_type: string, cta_text: string, extra: Partial<IClickLogBody> = {}) => {
      const body: IClickLogBody = {
        event_type,
        cta_text,
        scr_id: VOICE_EMR_SCREEN_ID,
        timestamp: Date.now(),
        user_id: userId,
        vms_user_id: userEmail,
        client_id: clientId ?? undefined,
        pet_id: petId ?? undefined,
        clinic_id: mappedClinicId,
        ...extra,
      };
      clickLogAPI(body);
    },
    [clickLogAPI, userId, userEmail, mappedClinicId, clientId, petId],
  );
};
